
import AppStockLocationSelect from "@/components/StockLocationSelect.vue";
import AppContactSearch from "@/components/contacts/ContactSearchWithBrowse.vue";
import AppCustomTable from "@/components/common/CustomTable.vue";
import { Contact, SizeTableEntry, StockIn } from "@/models/base";
import { BookYearService } from "@/services/book-year-service";
import { SizeTableService } from "@/services/size-table-service";
import { DossierConfigService } from "@/services";
import Vue from "vue";

// Bulk stock-in editor for a product with a size table: shared fields (location, date,
// buy price, supplier) plus one quantity input per size. Emits one StockIn per size that
// has a non-zero quantity via the "input" event.
export default Vue.extend({
	data() {
		return {
			locationID: 1,
			dateCreated: new Date(),
			buyPrice: 0,
			supplier: null as Contact | null,
			sizeEntries: [] as SizeTableEntry[],
			qtyByEntry: {} as Record<number, number>,
			DecimalSettings: DossierConfigService.getDecimalSettings()
		};
	},
	props: {
		// Base StockIn carrying the shared defaults (product, sku, supplier, buy price).
		value: {
			required: true,
			type: Object as () => StockIn
		}
	},
	computed: {
		maxDate():Date {
			return new Date(BookYearService.selectedBookYear, 11, 31);
		},
		totalQty():number {
			return this.sizeEntries.reduce((sum, entry) => sum + (this.qtyByEntry[entry.ID] || 0), 0);
		}
	},
	methods: {
		async loadSizeEntries() {
			if (!this.value.Product || !this.value.Product.SizeTableID) {
				this.sizeEntries = [];
				return;
			}
			let table = await SizeTableService.getById(this.value.Product.SizeTableID);
			this.sizeEntries = table.getSortedEntries();
			// Initialise every quantity to 0 up-front so the v-model bindings are reactive.
			let qty:Record<number, number> = {};
			for (let entry of this.sizeEntries) {
				qty[entry.ID] = 0;
			}
			this.qtyByEntry = qty;
		},
		selectSupplier(supplier:Contact | null) {
			this.supplier = supplier;
			this.emitStocks();
		},
		// Builds one StockIn per size with a non-zero quantity, cloning the shared base.
		getStocks():StockIn[] {
			let result:StockIn[] = [];
			for (let entry of this.sizeEntries) {
				let qty = this.qtyByEntry[entry.ID] || 0;
				if (!qty) continue;
				let stock = new StockIn(this.value);
				stock.ID = 0;
				stock.Qty = qty;
				stock.LocationID = this.locationID;
				stock.DateCreated = this.dateCreated;
				stock.UnitBuyPrice = this.buyPrice;
				stock.SizeTableEntryID = entry.ID;
				stock.SizeTableEntry = entry;
				if (this.supplier) {
					stock.Supplier = this.supplier;
					stock.SupplierID = this.supplier.ID;
				}
				result.push(stock);
			}
			return result;
		},
		emitStocks() {
			this.$emit("input", this.getStocks());
		},
		focusInput(index:number) {
			let inputs = this.$refs.qtyInputs as any[];
			if (!inputs || !inputs[index]) return;
			inputs[index].focus();
		},
		// Move focus to the next size input on Enter (no-op past the last row).
		focusNext(index:number) {
			this.focusInput(index + 1);
		}
	},
	async mounted() {
		this.locationID = this.value.LocationID || 1;
		this.dateCreated = this.value.DateCreated || new Date();
		if (this.dateCreated > this.maxDate) {
			this.dateCreated = this.maxDate;
		}
		this.buyPrice = this.value.UnitBuyPrice || 0;
		this.supplier = this.value.Supplier || null;
		await this.loadSizeEntries();
		this.emitStocks();
		// Autofocus the first size quantity input once the rows are rendered.
		await this.$nextTick();
		this.focusInput(0);
	},
	components: {
		AppStockLocationSelect,
		AppContactSearch,
		AppCustomTable
	},
	i18nEx: {
		componentPrefix: "components.stock"
	}
});
